import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { Button, IconButton } from "../../Ui/index.jsx";
import { apiPost } from "../../Api/Client.js";
import { safeAppRoute } from "../../Api/safeAppRoute.js";
import { formatDateTime } from "../../Shared/ScreenUtils.jsx";

const SHOWN = 8;

// Where "Review" takes the person. A notification that carries its own page (e.g. "you now have access to X") goes
// straight there; the rest go by what they are about.
function pathFor(item) {
  const route = safeAppRoute(item.metadata?.route);
  if (route) return route;
  const kind = String(item.resource_type || item.category || "").toLowerCase();
  const id = item.resource_id || item.metadata?.project || item.metadata?.project_id;
  if (kind.includes("project") && id) return "/projects/";
  if (kind.includes("leave")) return "/leave/apply/";
  return "/notifications/";
}

// The bell in the top bar: a count of unread ones and a list of the latest.
export function NotificationBell({ notifications = [], navigate, reload }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [failedId, setFailedId] = useState("");
  const [markAllBusy, setMarkAllBusy] = useState(false);
  const [optimisticRead, setOptimisticRead] = useState(false);
  const ref = useRef(null);
  const unread = optimisticRead ? [] : notifications.filter((item) => !item.is_read);
  const rows = [...notifications].sort((l, r) => new Date(r.created_at || 0) - new Date(l.created_at || 0)).slice(0, SHOWN);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDocClick); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const review = async (item) => {
    setBusyId(String(item.id));
    setFailedId("");
    try {
      if (!item.is_read) await apiPost(`/MainApp/Notifications/${item.id}/mark-read/`, {});
      reload(["notifications"]);
      navigate(pathFor(item));
      setOpen(false);
    } catch {
      // The button says so; the person presses it again.
      setFailedId(String(item.id));
    } finally { setBusyId(""); }
  };

  const markAllRead = async () => {
    if (markAllBusy) return;
    setMarkAllBusy(true);
    setOptimisticRead(true);
    try {
      await apiPost("/MainApp/Notifications/mark-all-read/", {});
      setOptimisticRead(false);
      reload(["notifications"]);
    } catch {
      setOptimisticRead(false);
    }
    setMarkAllBusy(false);
  };

  return (
    <div className="Atg-Notif-Bell" ref={ref}>
      <IconButton label="Notifications" active={open} onClick={() => setOpen((v) => !v)}>
        <Bell size={16} />
        {unread.length > 0 && <span className="Atg-Notif-Count">{unread.length}</span>}
      </IconButton>
      {open && (
        <div className="Atg-Notif-Popover">
          <div className="Atg-Notif-Head">
            <strong>Notifications</strong>
            <span>{unread.length} unread</span>
            {unread.length > 0 && <Button variant="link" onClick={markAllRead}>Mark all read</Button>}
            <Button variant="link" onClick={() => { setOpen(false); navigate("/notifications/"); }}>View all</Button>
          </div>
          <div className="Atg-Notif-List">
            {rows.map((item) => (
              <div key={item.id} className={item.is_read ? "Atg-Notif-Item" : "Atg-Notif-Item Unread"}>
                <div>
                  <strong>{item.title || item.category || "Notification"}</strong>
                  <p>{item.message || item.description || "Open this notification."}</p>
                  <small>{formatDateTime(item.created_at)}</small>
                </div>
                <Button size="sm" onClick={() => review(item)} disabled={busyId === String(item.id)}>
                  {busyId === String(item.id) ? "Opening" : failedId === String(item.id) ? "Try again" : "Review"}
                </Button>
              </div>
            ))}
            {!rows.length && <div className="Atg-Notif-Empty">No notifications loaded.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
